'use strict'

import { TimelineMax } from 'gsap';
import { CLASS_ACTIVE } from './helpers';

/**
 * Share element related functionality.
 */
const share = {

  /**
   * Timeline object which holds animation for this module.
   */
  timeline: new TimelineMax({
    paused: true,
  }),

  /**
   * Dom related variables
   */
  dom: {
    elements: {
      linksWrapper: document.querySelector('.body-footer .share .links'),
      alternatives: document.querySelectorAll('.body-footer .share .links li > *'),
      wrapper: document.querySelector('.body-footer .share'),
      linkToClipboard: document.querySelector('.body-footer .share .links .copy-to-clipboard'),
      facebook: document.querySelector('.body-footer .share .links .facebook'),
      twitter: document.querySelector('.body-footer .share .links .twitter'),
      linkedIn: document.querySelector('.body-footer .share .links .linkedin')
    },
    timeouts: {
      closeDelayId: null
    }
  },

  /**
   * Copies current url to clipboard.
   */
  eventClickLinkToClipboard: function () {
    const textarea = document.createElement('textarea');
    textarea.value = this.dom.elements.linkToClipboard.dataset.url;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);

    // Show notification about that url is copied
    if (this.dom.elements.clipboardMessage) {
      this.dom.elements.clipboardMessage.classList.add('show');
    }
  },

  /**
   * Updates link which is copied after copy to clipboard is clicked.
   * 
   * @param {string} link Url which is set to be copied.
   */
  setLinkToBeCopiedToClipboard: function (link) {
    this.dom.elements.linkToClipboard.dataset.url = link;
  },

  /**
   * Sets new url for Facebook share button.
   * 
   * @param {string} url Url for shared web page
   */
  setFacebookShareUrl: function (url) {
    this.dom.elements.facebook.setAttribute('href', `https://www.facebook.com/sharer.php?u=${encodeURIComponent(url)}`);
    this.dom.elements.facebook.dataset.url = url;
  },

  /**
   * Sets new url for LinkedIn share button.
   * 
   * @param {string} url Url for shared web page
   * @param {string} title Shared title
   * @param {string} summary Shared summary text
   */
  setLinkedInShareUrl: function (url, title, summary) {
    this.dom.elements.linkedIn.setAttribute('href', `http://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`);
    this.dom.elements.linkedIn.dataset.url = url;
  },

  /**
   * Sets new url for Twitter share button
   * 
   * @param {string} url Url for shared web page
   * @param {string} title Title text
   */
  setTwitterShareUrl: function (url, title) {
    this.dom.elements.twitter.setAttribute('href', `http://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`);
    this.dom.elements.twitter.dataset.url = url;
  },

  /**
   * Function on onMouseEnter event. Plays share animation.
   */
  onMouseEnter: function () {
    this.timeline.play();
  },

  /**
   * Function on onMouseLeave event. Reverses share animation.
   */
  onMouseLeave: function() {
    this.timeline.reverse();
  },

  /**
   * Function on onClick event. Plays or reverses animation based on timeline object's progress.
   * Shows copied to clipboard message if it was clicked.
   * 
   * @param {Object} e Javascript onClick event 
   */
  onClick: function (e) {
    if (this.timeline.progress() === 1) {
      const delay = e.target.closest('.copy-to-clipboard') ? 3000 : 0;
      this.dom.timeouts.closeDelayId = setTimeout(() => {
        if (this.dom.elements.clipboardMessage) {
          this.dom.elements.clipboardMessage.classList.remove('show');
        }
        this.timeline.reverse();
      }, delay);
    } else {
      this.timeline.play();
    }
  },

  /**
   * Creates timeline for share animations.
   */
  createTimeline: function () {

    const onStart = () => {
      this.dom.elements.wrapper.classList.add(CLASS_ACTIVE);
    };
    this.timeline.eventCallback('onStart', onStart);

    const onReverseComplete = () => {
      // If copy to clipboard was clicked show notification.
      this.dom.elements.wrapper.classList.remove(CLASS_ACTIVE);
      if (this.dom.elements.clipboardMessage) {
        this.dom.elements.clipboardMessage.classList.remove('show');
      }
      if (this.dom.timeouts.closeDelayId) {
        clearTimeout(this.dom.timeouts.closeDelayId);
      }
    };
    this.timeline.eventCallback('onReverseComplete', onReverseComplete);

    // Show share alternatives
    this.timeline.fromTo(this.dom.elements.alternatives,
      {
        opacity: 0,
        top: '20px'
      },
      {
        opacity: 1,
        top: '0px',
        ease: 'power4.out',
        duration: 0.3,
        stagger: 0.05
      }
    );
  },

  /**
   * Initializes module. Creates timeline for animations and adds event listeners.
   */
  init: function () {

    this.createTimeline();

    const onMouseEnter = this.onMouseEnter.bind(this);
    this.dom.elements.wrapper.addEventListener('mouseenter', onMouseEnter);

    const onMouseLeave = this.onMouseLeave.bind(this);
    this.dom.elements.wrapper.addEventListener('mouseleave', onMouseLeave);

    const eventClickLinkToClipboard = this.eventClickLinkToClipboard.bind(this);
    this.dom.elements.linkToClipboard.addEventListener('click', eventClickLinkToClipboard);

    // Add copy to clipboard element
    const message = document.createElement('div');
    message.classList.add('share-message');
    message.innerText = this.dom.elements.linkToClipboard.dataset.text;
    this.dom.elements.clipboardMessage = message;
    this.dom.elements.linkToClipboard.parentElement.appendChild(message);

    const onClick = this.onClick.bind(this);
    this.dom.elements.wrapper.addEventListener('click', onClick);
  }
}

module.exports = share;
